import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader, Database, FileText, Workflow, AlertTriangle, Trash2, PlayCircle, Users, Clock, ShieldAlert, RotateCcw, RefreshCw } from 'lucide-react';
import {
  getCustomerOverview,
  getSentryIssues,
  clearTenantCache,
  listCustomerFiles,
  activateCustomerFile,
  deactivateCustomer,
  reactivateCustomer,
} from '../../../services/apiClient';
import { createCustomerETLApi } from '../../etl-monitor/api/etlServiceApi';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '../../../components/ui/card';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../../components/ui/select';
import StatusChip from './StatusChip';
import ConfirmAction from './ConfirmAction';

const CardIcon = ({ icon: Icon }) => (
  <span className="flex-shrink-0 p-2 bg-primary-50 rounded-lg">
    <Icon className="w-5 h-5 text-primary-600" />
  </span>
);

// Sentry issue level -> StatusChip tone. Unmapped levels (rare: "sample", etc.) fall back to gray.
const SENTRY_LEVEL_TONE = { fatal: 'red', error: 'red', warning: 'amber', info: 'blue', debug: 'gray' };
const SENTRY_STATUS_TONE = { unresolved: 'red', resolved: 'emerald', ignored: 'gray' };

/**
 * Overview tab of the tenant admin console — cache stats, active file,
 * ETL job/schedule status, and Sentry recent-errors visibility
 * for one tenant, plus the actions to clear that tenant's cache, trigger a
 * manual ETL run, and activate a different file for that tenant.
 */
const OverviewTab = ({ customerId, customer, onSaved }) => {
  const { t } = useTranslation('admin');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [sentryData, setSentryData] = useState(null);
  const [sentryLoading, setSentryLoading] = useState(false);

  const [clearingCache, setClearingCache] = useState(false);
  const [cacheActionErr, setCacheActionErr] = useState(null);

  const [togglingActive, setTogglingActive] = useState(false);
  const [activeToggleErr, setActiveToggleErr] = useState(null);

  const [triggeringEtl, setTriggeringEtl] = useState(false);
  const [etlActionErr, setEtlActionErr] = useState(null);
  const [lastTriggeredJobId, setLastTriggeredJobId] = useState(null);

  const [files, setFiles] = useState([]);
  const [selectedFileType, setSelectedFileType] = useState('');
  const [activating, setActivating] = useState(false);
  const [activateActionErr, setActivateActionErr] = useState(null);

  const etlApi = useMemo(() => createCustomerETLApi(customerId), [customerId]);

  const loadOverview = async () => {
    setLoading(true);
    setErr(null);
    try {
      const overview = await getCustomerOverview(customerId);
      setData(overview);
    } catch (e) {
      setErr(t('errors.loadOverview', { detail: e.message }));
    } finally {
      setLoading(false);
    }
  };

  const loadSentryIssues = async () => {
    if (!customerId) return;
    setSentryLoading(true);
    try {
      const result = await getSentryIssues(customerId);
      setSentryData(result);
    } catch {
      setSentryData(null); // non-fatal: panel just stays hidden
    } finally {
      setSentryLoading(false);
    }
  };

  useEffect(() => {
    loadSentryIssues();
  }, [customerId]);

  useEffect(() => {
    if (!customerId) return;
    loadOverview();
    setLastTriggeredJobId(null);
    setCacheActionErr(null);
    setEtlActionErr(null);
    setActivateActionErr(null);
  }, [customerId]);

  useEffect(() => {
    if (!customerId) return;
    listCustomerFiles(customerId)
      .then((list) => {
        setFiles(list);
        setSelectedFileType('');
      })
      .catch(() => setFiles([]));
  }, [customerId]);

  const handleClearCache = async () => {
    setClearingCache(true);
    setCacheActionErr(null);
    try {
      await clearTenantCache(customerId);
      await loadOverview();
    } catch (e) {
      setCacheActionErr(e.message);
    } finally {
      setClearingCache(false);
    }
  };

  const handleTriggerEtl = async () => {
    setTriggeringEtl(true);
    setEtlActionErr(null);
    try {
      const result = await etlApi.triggerRun();
      setLastTriggeredJobId(result?.job_id || null);
      setTimeout(loadOverview, 2000);
    } catch (e) {
      setEtlActionErr(e.response?.data?.detail || e.message);
    } finally {
      setTriggeringEtl(false);
    }
  };

  const handleToggleActive = async () => {
    setTogglingActive(true);
    setActiveToggleErr(null);
    try {
      const updated = customer?.is_active
        ? await deactivateCustomer(customerId)
        : await reactivateCustomer(customerId);
      onSaved?.(updated.is_active);
    } catch (e) {
      setActiveToggleErr(e.message);
    } finally {
      setTogglingActive(false);
    }
  };

  const handleActivateFile = async () => {
    if (!selectedFileType) return;
    setActivating(true);
    setActivateActionErr(null);
    try {
      await activateCustomerFile(customerId, selectedFileType);
      setSelectedFileType('');
      await loadOverview();
    } catch (e) {
      setActivateActionErr(e.message);
    } finally {
      setActivating(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="flex-1">
        <Loader className="w-4 h-4 animate-spin" />
      </div>
    );
  }

  if (err) {
    return (
      <div className="flex-1">
        <div className="text-sm text-error-600">{err}</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex-1">
        <div className="text-sm text-gray-500">{t('overview.noData')}</div>
      </div>
    );
  }

  const etlHasActiveJob = data.etl?.recent_jobs?.some((j) => j.status === 'pending' || j.status === 'running');

  return (
    <div className="flex-1">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Cache */}
        <Card>
          <CardHeader className="p-4 pb-2 flex-row items-center gap-3 space-y-0">
            <CardIcon icon={Database} />
            <CardTitle className="text-sm font-medium text-gray-900">{t('overview.cache')}</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0 text-sm text-gray-600">
            {data.cache && data.cache.entry_count !== undefined
              ? t('overview.cacheEntries', { count: data.cache.entry_count })
              : t('overview.cacheUnavailable')}
            {cacheActionErr && <div className="mt-2 text-xs text-error-600">{cacheActionErr}</div>}
          </CardContent>
          <CardFooter className="p-4 pt-0">
            <ConfirmAction
              label={t('overview.clearCache')}
              confirmLabel={t('overview.confirm')}
              cancelLabel={t('overview.cancel')}
              icon={Trash2}
              variant="outline"
              pending={clearingCache}
              onConfirm={handleClearCache}
            />
          </CardFooter>
        </Card>

        {/* Active file */}
        <Card>
          <CardHeader className="p-4 pb-2 flex-row items-center gap-3 space-y-0">
            <CardIcon icon={FileText} />
            <CardTitle className="text-sm font-medium text-gray-900">{t('overview.activeFile')}</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0 text-sm text-gray-600 space-y-3">
            <div>
              {data.active_file
                ? t('overview.activatedAt', { when: data.active_file.activated_at }) +
                  (data.active_file.file_type ? ` (${data.active_file.file_type})` : '')
                : t('overview.noActiveFile')}
            </div>
            {files.length > 0 ? (
              <Select value={selectedFileType} onValueChange={setSelectedFileType}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder={t('overview.selectFilePlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {files.map((f) => (
                    <SelectItem key={f.file_type} value={f.file_type}>
                      {f.display_name || f.file_type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="text-xs text-gray-400">{t('overview.noFilesForTenant')}</div>
            )}
            {activateActionErr && <div className="text-xs text-error-600">{activateActionErr}</div>}
          </CardContent>
          <CardFooter className="p-4 pt-0">
            <ConfirmAction
              label={t('overview.activateFile')}
              confirmLabel={t('overview.confirm')}
              cancelLabel={t('overview.cancel')}
              pending={activating}
              disabled={!selectedFileType}
              onConfirm={handleActivateFile}
            />
          </CardFooter>
        </Card>

        {/* ETL */}
        <Card>
          <CardHeader className="p-4 pb-2 flex-row items-center gap-3 space-y-0">
            <CardIcon icon={Workflow} />
            <CardTitle className="text-sm font-medium text-gray-900">{t('overview.etlStatus')}</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0 text-sm text-gray-600 space-y-2">
            <div className="flex flex-wrap gap-2">
              <StatusChip tone={data.etl?.reachable ? 'emerald' : 'red'}>
                {data.etl?.reachable ? t('board.etlReachable') : t('board.etlUnreachable')}
              </StatusChip>
              <StatusChip tone={data.scheduled_data_enabled ? 'emerald' : 'amber'}>
                {data.scheduled_data_enabled ? t('board.scheduled') : t('board.notScheduled')}
              </StatusChip>
            </div>
            <div>
              {data.scheduled_data_enabled
                ? data.next_fire_time
                  ? t('overview.scheduleEnabled', { when: data.next_fire_time })
                  : t('overview.scheduleEnabledNoNextRun')
                : t('overview.scheduleDisabled')}
            </div>
            {!data.etl ? (
              <div>{t('overview.etlUnavailable')}</div>
            ) : !data.etl.reachable ? (
              <div>{t('overview.etlUnreachable')}</div>
            ) : !data.etl.recent_jobs?.length ? (
              <div>{t('overview.etlNoJobs')}</div>
            ) : (
              <div>
                <div className="text-xs font-medium text-gray-500 mb-1">{t('overview.recentJobs')}</div>
                {/* Per-job status color (spec's status-color vocabulary): a
                    failed job is the single most operationally useful signal
                    here, so it must not read the same as a successful one. */}
                <ul className="text-sm space-y-0.5">
                  {data.etl.recent_jobs.map((job) => (
                    <li
                      key={job.job_id}
                      className={
                        job.status === 'success'
                          ? 'text-emerald-700'
                          : job.status === 'failed'
                          ? 'text-red-600'
                          : 'text-gray-600'
                      }
                    >
                      {t('overview.etlLastJob', { status: job.status, jobId: job.job_id })}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {lastTriggeredJobId && (
              <div className="text-xs text-primary-600">{t('overview.jobTriggered', { jobId: lastTriggeredJobId })}</div>
            )}
            {etlActionErr && <div className="text-xs text-error-600">{etlActionErr}</div>}
          </CardContent>
          <CardFooter className="p-4 pt-0">
            <ConfirmAction
              label={t('overview.triggerEtl')}
              confirmLabel={t('overview.confirm')}
              cancelLabel={t('overview.cancel')}
              icon={PlayCircle}
              disabled={!data.etl?.reachable || etlHasActiveJob}
              pending={triggeringEtl}
              onConfirm={handleTriggerEtl}
            />
          </CardFooter>
        </Card>

        {/* Recent errors (Sentry) — hidden entirely when not configured */}
        {sentryData?.available && (
          <Card className="md:col-span-2">
            <CardHeader className="p-4 pb-2 flex-row items-center gap-3 space-y-0">
              <CardIcon icon={AlertTriangle} />
              <CardTitle className="text-sm font-medium text-gray-900 flex-1">{t('overview.recentErrors')}</CardTitle>
              <button
                type="button"
                onClick={loadSentryIssues}
                disabled={sentryLoading}
                aria-label={t('overview.refreshErrors')}
                title={t('overview.refreshErrors')}
                className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${sentryLoading ? 'animate-spin' : ''}`} />
              </button>
            </CardHeader>
            <CardContent className="p-4 pt-0 text-sm text-gray-600">
              {sentryData.issues.length === 0 ? (
                <div>{t('overview.noRecentErrors')}</div>
              ) : (
                <ul className="space-y-2.5">
                  {sentryData.issues.map((issue) => (
                    <li
                      key={issue.permalink || issue.title}
                      className="border-b border-gray-100 pb-2.5 last:border-0 last:pb-0"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <a
                          href={issue.permalink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:underline truncate"
                        >
                          {issue.title}
                        </a>
                        <span className="text-error-600 text-xs shrink-0">
                          {/* count may be null (the backend schema allows it);
                              i18next would then render a bare "×" with no number. */}
                          {t('overview.errorCount', { count: issue.count ?? '0' })}
                        </span>
                      </div>
                      {issue.culprit && (
                        <div className="text-xs text-gray-400 truncate mt-0.5">{issue.culprit}</div>
                      )}
                      <div className="flex items-center gap-1.5 flex-wrap mt-1">
                        {issue.level && (
                          <StatusChip tone={SENTRY_LEVEL_TONE[issue.level] || 'gray'}>{issue.level}</StatusChip>
                        )}
                        {issue.status && (
                          <StatusChip tone={SENTRY_STATUS_TONE[issue.status] || 'gray'}>{issue.status}</StatusChip>
                        )}
                        {issue.user_count != null && (
                          <StatusChip tone="gray" icon={Users}>
                            {t('overview.errorUserCount', { count: issue.user_count })}
                          </StatusChip>
                        )}
                        {issue.last_seen && (
                          <StatusChip tone="gray" icon={Clock}>
                            {t('overview.errorLastSeen', { when: issue.last_seen })}
                          </StatusChip>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        )}

        {/* Danger zone: deactivate/reactivate this tenant (soft delete) */}
        <Card className="md:col-span-2 border-error-200">
          <CardHeader className="p-4 pb-2 flex-row items-center gap-3 space-y-0">
            <CardIcon icon={ShieldAlert} />
            <CardTitle className="text-sm font-medium text-gray-900">{t('overview.dangerZone')}</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0 text-sm text-gray-600">
            {customer?.is_active === false ? t('overview.tenantInactive') : t('overview.deactivateExplain')}
            {activeToggleErr && <div className="mt-2 text-xs text-error-600">{activeToggleErr}</div>}
          </CardContent>
          <CardFooter className="p-4 pt-0">
            <ConfirmAction
              label={customer?.is_active === false ? t('overview.reactivateTenant') : t('overview.deactivateTenant')}
              confirmLabel={t('overview.confirm')}
              cancelLabel={t('overview.cancel')}
              icon={customer?.is_active === false ? RotateCcw : Trash2}
              variant={customer?.is_active === false ? 'outline' : 'destructive'}
              pending={togglingActive}
              onConfirm={handleToggleActive}
            />
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default OverviewTab;
